import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { PunishedPortfolioReportService, PunishedPortfolioReportItem } from '../../../services/punished-portfolio-report.service';
import { ProjectService } from '../../../services/project.service';
import { ToastService } from '../../../services/toast.service';
import { PortfolioMovementType } from '../../../services/portfolio-movement.service';
import { ReportJobsService } from '../../../services/report-jobs.service';
import { ReportHubService } from '../../../services/report-hub.service';

@Component({
  selector: 'app-punished-portfolio-report',
  templateUrl: './punished-portfolio-report.component.html',
  styleUrls: ['./punished-portfolio-report.component.scss']
})
export class PunishedPortfolioReportComponent implements OnInit, OnDestroy {
  filterForm: FormGroup;
  reportData: PunishedPortfolioReportItem[] = [];
  projects: any[] = [];

  isLoading = false;
  isDownloading = false;
  searchPerformed = false;

  private reportHubSub!: Subscription;
  private pendingJobId: string | null = null;

  // Estadísticas
  stats = {
    totalWithdrawals: 0,
    totalDeferrals: 0,
    withdrawalsAmount: 0,
    deferralsAmount: 0,
    totalAmount: 0
  };

  constructor(
    private fb: FormBuilder,
    private reportService: PunishedPortfolioReportService,
    private projectService: ProjectService,
    private toastService: ToastService,
    private reportJobsService: ReportJobsService,
    private reportHubService: ReportHubService
  ) {
    this.filterForm = this.fb.group({
      startDate: [null],
      endDate: [null],
      projectId: [null]
    });
  }

  ngOnInit(): void {
    this.loadProjects();
    this.loadReport();

    this.reportHubSub = this.reportHubService.reportStatusChanged$.subscribe(notification => {
      if (notification.jobId !== this.pendingJobId) return;

      if (notification.status === 'Completed') {
        this.isDownloading = false;
        this.toastService.showToast('success', 'Reporte generado exitosamente. Descargando...');
        this.reportJobsService.downloadReport(notification.jobId, notification.fileName || undefined);
        this.pendingJobId = null;
      } else if (notification.status === 'Failed') {
        this.isDownloading = false;
        this.toastService.showToast('error', `Error al generar el reporte: ${notification.errorMessage || 'Desconocido'}`);
        this.pendingJobId = null;
      } else if (notification.status === 'Processing') {
        this.toastService.showToast('info', 'Procesando reporte de cartera castigada...');
      }
    });
  }

  ngOnDestroy(): void {
    this.reportHubSub?.unsubscribe();
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  loadProjects(): void {
    this.projectService.getAll().subscribe({
      next: (response) => {
        this.projects = this.extractData(response);
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar proyectos');
      }
    });
  }

  loadReport(): void {
    this.isLoading = true;
    this.searchPerformed = true;
    
    const { startDate, endDate, projectId } = this.filterForm.value;
    
    this.reportService.getReport(
      startDate ? new Date(startDate) : undefined,
      endDate ? new Date(endDate) : undefined,
      projectId || undefined
    ).subscribe({
      next: (response) => {
        this.reportData = response || [];
        this.calculateStats();
        this.isLoading = false;
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar reporte');
        this.isLoading = false;
      }
    });
  }

  calculateStats(): void {
    this.stats = {
      totalWithdrawals: this.reportData.filter(r => r.tipoMovimiento === PortfolioMovementType.Withdrawal).length,
      totalDeferrals: this.reportData.filter(r => r.tipoMovimiento === PortfolioMovementType.Deferral).length,
      withdrawalsAmount: this.reportData
        .filter(r => r.tipoMovimiento === PortfolioMovementType.Withdrawal)
        .reduce((sum, r) => sum + r.saldoPendiente, 0),
      deferralsAmount: this.reportData
        .filter(r => r.tipoMovimiento === PortfolioMovementType.Deferral)
        .reduce((sum, r) => sum + r.saldoPendiente, 0),
      totalAmount: this.reportData.reduce((sum, r) => sum + r.saldoPendiente, 0)
    };
  }

  downloadExcel(): void {
    this.isDownloading = true;

    const { startDate, endDate, projectId } = this.filterForm.value;

    this.reportJobsService.queuePunishedPortfolioReport({
      startDate: startDate ? new Date(startDate) : null,
      endDate: endDate ? new Date(endDate) : null,
      projectId: projectId || null
    }).subscribe({
      next: (response) => {
        this.pendingJobId = response.jobId;
        this.toastService.showToast('info', 'Generando reporte de cartera castigada en segundo plano...');
      },
      error: () => {
        this.toastService.showToast('error', 'Error al solicitar reporte');
        this.isDownloading = false;
      }
    });
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.loadReport();
  }

  getMovementTypeClass(type: PortfolioMovementType): string {
    switch (type) {
      case PortfolioMovementType.Withdrawal:
        return 'bg-red-100 text-red-800';
      case PortfolioMovementType.Deferral:
        return 'bg-yellow-100 text-yellow-800';
      case PortfolioMovementType.WithdrawalReversal:
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
}
